import AppLoader from 'components/AppLoader';
import _, { isEmpty } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { getEmptyCancellableRequest } from 'restAPI';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { TierDto } from 'types/api/tierBasedTagsDto';
import { AppNavConfig } from 'types/tenantConfig';
import { BASE_SEVERITIES } from 'views/Findings/constants/severity';
import type { RiskScoreEscalationConfig } from 'views/GlobalSettings/RiskScoreCeiling';

import { useFeatureFlag } from './FeatureFlagContext';
import { useGlobalFilterContext } from './GlobalFilterContext';

type BU = {
  product: string;
  subProduct: string;
  project: string;
  group: string;
  assessment: string;
  open: string;
  confirmed: string;
  acceptRisk: string;
  falsePositive: string;
  mitigated: string;
  staging: string;
  production: string;
  businessOwner: string;
  securityOwner: string;
  engineeringOwner: string;
  supportOwner: string;
  complianceOwner: string;
  riskScore: string;
  findingScore: string;
  assetScore: string;
  category: string;
  severity: string;
  team: string;
  owner: string;
};

export type AccountDefaultsConfig = {
  disabledMenuItems: string[];
  sidebarConfig: AppNavConfig;
  entry_page?: string | null;
  default_dashboard?: string | null;
  default_view?: string | null;
};

interface GlobalSettingsState extends BU {
  findingsStorageTime: string;
  dateFormat: string;
  suppressed: string;
  stepToReproduce: string;
  impact: string;
  mitigation: string;
  triage: string;
  remediation: string;
  resolution: string;
  excludeSeverities: string[];
  mitigatedStatus: string[];
  calculateRiskScoreAsProduct: boolean;
  controlledStatus: string;
  triageStatus: string;
  inProgressStatus: string;
  exception: string;
  goodRiskScore: string;
  fairRiskScore: string;
  poorRiskScore: string;
  veryPoorRiskScore: string;
  riskScoreRange: number[];
  maxRiskScore: number;
  cveRiskCalculationType: string;
  findingScoreAlgorithm: string;
  critical: string;
  high: string;
  medium: string;
  low: string;
  info: string;
  anyaAIAssistantEnabled: boolean;
  bu: Partial<BU>;
  disabledFindingStatus: string[];
  disabledEnvironment: string[];
  disabledMenuItems: string[];
  accountDefaultsConfig?: AccountDefaultsConfig;
  riskScoreEnabled: boolean;
  tierBasedTags: TierDto[];
  calculationType: string;
  subProductCalculationType: string;
  isSLAEnabled: boolean;
  acceptRiskReasons: string[];
  controlledReasons: string[];
  riskScoreInverted?: boolean;
  hierarchySeverities?: Record<string, string[]>;
  escalationConfig: RiskScoreEscalationConfig | null;
  runbookFailureNotificationSeverity: string | undefined;
}

interface GlobalSettingsContextType extends GlobalSettingsState {
  findingBreakdownGroupByField: string;
  breakDownBySeverity: boolean;
  riskScoreRange: number[];
  maxRiskScore: number;
  cveRiskCalculationType: string;
  findingScoreAlgorithm: string;
  updateSettings: (settings: Partial<GlobalSettingsState>) => Promise<void>;
  updateDisableStatusSettings: (data: string[]) => Promise<void>;
  updateTierBasedTagsSettings: (data: TierDto[]) => Promise<void>;
  updateDisableEnvironmentSettings: (data: string[]) => Promise<void>;
  getAllowedSeverities: (severities: string[]) => string[];
  updateRiskScoreEnabled: (riskScoreEnabled: boolean) => void;
  updateRiskScoreRanges: (riskScoreRange: number[]) => void;
  updateSettingsAction: (data: unknown) => void;
  updateCveRiskCalculationType: (
    cveRiskCalculationType: string | undefined,
  ) => void;
  updateAnyaAIAssistantEnabled: (enabled: boolean) => void;
  updateRunbookFailureNotificationSeverity: (severity: string) => void;
  updateFindingByBreakdown: (findingBreakdownData: string) => Promise<void>;
  disabledEnvironment: string[];
  disabledMenuItems: string[];
  riskScoreEnabled: boolean;
  tierBasedTags: TierDto[];
  isSLAEnabled: boolean;
}

interface GlobalSettingsContextProviderProps {
  children: React.ReactNode;
  allowApiFetch?: boolean;
  forceAPIFetch?: boolean;
}

enum Actions {
  UPDATE_SETTINGS = 'update_filters',
  UPDATE_BU_SETTINGS = 'update_bu_settings',
}

type Action = Actions.UPDATE_SETTINGS | Actions.UPDATE_BU_SETTINGS;

const initialState: GlobalSettingsState = {
  findingsStorageTime: '1_month',
  dateFormat: 'MMDDYY',
  product: 'Group',
  subProduct: 'Subgroup',
  project: 'Engagement',
  group: 'Hierarchy',
  assessment: 'Pentest',
  open: 'Open',
  confirmed: 'Confirm',
  acceptRisk: 'Accept Risk',
  falsePositive: 'False +ve',
  mitigated: 'Remediated',
  suppressed: 'Suppressed',
  staging: 'Staging',
  production: 'Production',
  businessOwner: 'Business Owner',
  securityOwner: 'Security Owner',
  engineeringOwner: 'Engineering Owner',
  supportOwner: 'Support Owner',
  complianceOwner: 'Compliance Owner',
  owner: 'Owner',
  severity: 'Severity',
  category: 'Category',
  riskScore: 'Risk Score',
  findingScore: 'Finding Score',
  assetScore: 'Asset Score',
  team: 'Team',
  stepToReproduce: 'Steps To Reproduce',
  impact: 'Impact',
  mitigation: 'Mitigation',
  triage: 'Triage',
  remediation: 'Remediation',
  resolution: 'Resolution',
  excludeSeverities: [],
  mitigatedStatus: [],
  calculateRiskScoreAsProduct: true,
  controlledStatus: 'Mitigated',
  triageStatus: 'Triage',
  inProgressStatus: 'In Progress',
  exception: 'Exception',
  goodRiskScore: 'Minimal Risk',
  fairRiskScore: 'Moderate Risk',
  poorRiskScore: 'High Risk',
  veryPoorRiskScore: 'Critical Risk',
  riskScoreRange: [],
  maxRiskScore: 1000,
  cveRiskCalculationType: 'AATI_CTI_BASED',
  findingScoreAlgorithm: '',
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
  anyaAIAssistantEnabled: false,
  bu: {},
  disabledFindingStatus: [],
  disabledEnvironment: [],
  disabledMenuItems: [],
  tierBasedTags: [],
  riskScoreEnabled: false,
  calculationType: 'RISKY',
  subProductCalculationType: 'RISKY',
  isSLAEnabled: false,
  acceptRiskReasons: [],
  controlledReasons: [],
  riskScoreInverted: false,
  escalationConfig: null,
  runbookFailureNotificationSeverity: undefined,
};

export { initialState as GlobalSettingsInitialValues };

const customTitleKeys: Array<keyof GlobalSettingsState> = [
  'product',
  'subProduct',
  'project',
  'group',
  'assessment',
  'open',
  'confirmed',
  'acceptRisk',
  'falsePositive',
  'mitigated',
  'staging',
  'production',
  'businessOwner',
  'securityOwner',
  'engineeringOwner',
  'supportOwner',
  'complianceOwner',
  'riskScore',
  'findingScore',
  'assetScore',
  'category',
  'severity',
  'team',
  'stepToReproduce',
  'impact',
  'mitigation',
  'triage',
  'remediation',
  'resolution',
  'suppressed',
  'controlledStatus',
  'triageStatus',
  'inProgressStatus',
  'exception',
  'goodRiskScore',
  'fairRiskScore',
  'poorRiskScore',
  'veryPoorRiskScore',
  'owner',
  'critical',
  'high',
  'low',
  'medium',
  'info',
];

let GLOBAL_SETTINGS = {} as GlobalSettingsState;

const GlobalSettingsContext = createContext<GlobalSettingsContextType>({
  ...initialState,
  findingBreakdownGroupByField: 'severity',
  breakDownBySeverity: true,
  cveRiskCalculationType: '',
  findingScoreAlgorithm: '',
  updateSettings: () => Promise.resolve(),
  getAllowedSeverities: (severities) => severities,
  updateDisableStatusSettings: () => Promise.resolve(),
  updateTierBasedTagsSettings: () => Promise.resolve(),
  updateDisableEnvironmentSettings: () => Promise.resolve(),
  updateRiskScoreEnabled: () => false,
  updateRiskScoreRanges: () => {},
  updateSettingsAction: () => {},
  updateCveRiskCalculationType: () => undefined,
  updateAnyaAIAssistantEnabled: () => {},
  updateFindingByBreakdown: () => Promise.resolve(),
  updateRunbookFailureNotificationSeverity: () => {},
});

function reducer(
  state: GlobalSettingsState,
  action: { type: Action; payload: Partial<GlobalSettingsState> },
) {
  switch (action.type) {
    case Actions.UPDATE_SETTINGS:
      // Update this variable as well so that we don't have to reload the data for Modals,
      // as most modals are out of the root react context
      GLOBAL_SETTINGS = {
        ...state,
        ...action.payload,
      };
      return { ...GLOBAL_SETTINGS };
    case Actions.UPDATE_BU_SETTINGS:
      GLOBAL_SETTINGS = {
        ...state,
        bu: { ...action.payload },
      };
      return { ...GLOBAL_SETTINGS };
    default:
      return state;
  }
}

export const GlobalSettingsContextProvider = ({
  children,
  allowApiFetch = true,
}: GlobalSettingsContextProviderProps) => {
  const [state, dispatch] = useReducer(reducer, GLOBAL_SETTINGS);
  const { globalFilterBusinessUnit, globalFilterHash } =
    useGlobalFilterContext();
  const { excludeSeverities } = state;
  const showLoader = _.isEmpty(_.omit(state, 'bu'));
  const featureFlags = useFeatureFlag();
  const [breakDownBySeverityConfig, setBreakDownBySeverityConfig] = useState({
    breakDownBySeverity: true,
  });

  useEffect(() => {
    const { cancel, promise } = GlobalSettingAPI.getBreakDownBySeverity();
    promise.then((breakDownBySeverityConfig) => {
      setBreakDownBySeverityConfig(breakDownBySeverityConfig);
    });
    return cancel;
  }, []);

  useEffect(() => {
    if (!showLoader) {
      return;
    }

    // forceAPIFetch = to force refetch and not use from cache object.
    // Since severity breakdown isn't in cache
    if (allowApiFetch) {
      const promiseObj = GlobalSettingAPI.get();
      const promiseObj1 = featureFlags.SLA_V2
        ? GlobalSettingAPI.getGlobalSettingSlaStatus()
        : { promise: Promise.resolve(), cancel: () => {} };

      const configTypesToLoad = [
        'MITIGATED_STATUS',
        'RISK_SCORE',
        'DISABLED_FINDING_STATUS',
        'DISABLED_ENVIRONMENT',
        'RISK_SCORE_RATINGS_CONFIG',
        'FINDING_SCORE_CALCULATION_TYPE',
        'ENABLE_ANYA_AI_ASSISTANT',
        'ACCOUNT_DEFAULTS',
        'RISK_TIERS',
        'ACCEPT_RISK_REASON',
        'CONTROLLED_REASON',
        'RUNBOOK_FAILURE_NOTIFICATION_CONFIG',
      ];
      if (featureFlags.HIERARCHY_SEVERITY_MAPPING) {
        configTypesToLoad.push('HIERARCHY_SEVERITY_MAPPING');
      }
      if (
        featureFlags.RISK_SCORE_CEILING_ESCALATION ||
        featureFlags.RISK_SCORE_FINDING_ESCALATION
      ) {
        configTypesToLoad.push('RISK_SCORE_ESCALATION_CONFIG');
      }

      const promiseObj2 =
        GlobalSettingAPI.getTenantConfigInBatch(configTypesToLoad);

      Promise.all([
        promiseObj.promise,
        promiseObj1.promise,
        promiseObj2.promise,
      ]).then(([data, slaStatusData, { data: tenantConfigs }]) => {
        data = {
          ...(data ?? {}),
          ...(data.customTitles ?? {}),
          mitigatedStatus: tenantConfigs.MITIGATED_STATUS,
          riskScoreEnabled: tenantConfigs.RISK_SCORE,
          disabledFindingStatus: tenantConfigs.DISABLED_FINDING_STATUS,
          tierBasedTags: tenantConfigs.RISK_TIERS,
          disabledEnvironment:
            tenantConfigs.DISABLED_ENVIRONMENT?.disabledEnvironment,
          riskScoreRange:
            tenantConfigs.RISK_SCORE_RATINGS_CONFIG?.riskScoreRange,
          maxRiskScore: tenantConfigs.RISK_SCORE_RATINGS_CONFIG?.maxRiskScore,
          anyaAIAssistantEnabled:
            tenantConfigs.ENABLE_ANYA_AI_ASSISTANT?.enabled,
          cveRiskCalculationType:
            tenantConfigs.FINDING_SCORE_CALCULATION_TYPE?.data,
          findingScoreAlgorithm:
            tenantConfigs.FINDING_SCORE_CALCULATION_TYPE?.extraMetadata
              ?.findingScoreAlgorithm ?? '',
          hierarchySeverities: tenantConfigs.HIERARCHY_SEVERITY_MAPPING,
          disabledMenuItems: tenantConfigs.ACCOUNT_DEFAULTS?.disabledMenuItems,
          accountDefaultsConfig: tenantConfigs.ACCOUNT_DEFAULTS,
          isSLAEnabled: !featureFlags.SLA_V2
            ? true
            : slaStatusData?.SLA_RECALCULATION_TRIGGERS?.globalSlaEnabled,
          acceptRiskReasons: tenantConfigs.ACCEPT_RISK_REASON?.reasons,
          controlledReasons: tenantConfigs.CONTROLLED_REASON?.reasons,
          escalationConfig: tenantConfigs.RISK_SCORE_ESCALATION_CONFIG ?? null,
          runbookFailureNotificationSeverity:
            tenantConfigs.RUNBOOK_FAILURE_NOTIFICATION_CONFIG?.severity,
        };

        if (!_.isEmpty(data)) {
          customTitleKeys.forEach((key) => {
            data[key] = data[key + 'Name'] ?? initialState[key];
          });

          _.forEach(initialState, (value, key) => {
            if (_.has(data, key) && _.isNil(data[key])) {
              data[key] = initialState[key as keyof GlobalSettingsState];
            }
          });
          dispatch({ type: Actions.UPDATE_SETTINGS, payload: data });
        } else {
          dispatch({
            type: Actions.UPDATE_SETTINGS,
            payload: { ...initialState },
          });
        }
      }, _.noop);
      return () => {
        promiseObj.cancel();
        promiseObj1.cancel();
        promiseObj2.cancel();
      };
    } else {
      dispatch({ type: Actions.UPDATE_SETTINGS, payload: { ...initialState } });
    }
  }, [
    showLoader,
    allowApiFetch,
    featureFlags.HIERARCHY_SEVERITY_MAPPING,
    featureFlags.SLA_V2,
    featureFlags.RISK_SCORE_CEILING_ESCALATION,
    featureFlags.RISK_SCORE_FINDING_ESCALATION,
  ]);

  useEffect(() => {
    if (!_.isEmpty(globalFilterHash)) {
      const { promise, cancel } = GlobalSettingAPI.getBUCustomizedTitles(
        globalFilterBusinessUnit,
      );
      promise.then((res) => {
        dispatch({
          type: Actions.UPDATE_BU_SETTINGS,
          payload: !_.isEmpty(res.CUSTOM_TITLES?.config)
            ? JSON.parse(res.CUSTOM_TITLES.config)
            : {},
        });
      });
      return cancel;
    }
  }, [globalFilterHash, globalFilterBusinessUnit]);

  const updateSettings = useCallback(async (settings) => {
    const customTitles: { [key: string]: string } = {};
    customTitleKeys.forEach((key: keyof GlobalSettingsState) => {
      customTitles[key + 'Name'] = settings[key];
      settings[key + 'Name'] = settings[key];
    });
    const { promise } = GlobalSettingAPI.update(settings);
    const { promise: promise2 } =
      GlobalSettingAPI.updateCustomizedTitles(customTitles);
    await Promise.all([promise, promise2]);
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: settings,
    });
  }, []);

  const updateDisableStatusSettings = useCallback(async (data) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { disabledFindingStatus: data },
    });
  }, []);

  const updateTierBasedTagsSettings = useCallback(async (data) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { tierBasedTags: data },
    });
  }, []);

  const updateDisableEnvironmentSettings = useCallback(async (data) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { disabledEnvironment: data },
    });
  }, []);

  const getAllowedSeverities = useCallback(
    (severities = BASE_SEVERITIES) => {
      return _.values(severities).filter(
        (val) => excludeSeverities.indexOf(val.toUpperCase()) === -1,
      );
    },
    [excludeSeverities],
  );

  const updateRiskScoreEnabled = useCallback((riskScoreEnabled) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { riskScoreEnabled },
    });
  }, []);
  const updateRiskScoreRanges = useCallback((riskScoreRange) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { riskScoreRange },
    });
  }, []);
  const updateAnyaAIAssistantEnabled = useCallback((enabled) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { anyaAIAssistantEnabled: enabled },
    });
  }, []);
  const updateRunbookFailureNotificationSeverity = useCallback((severity) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { runbookFailureNotificationSeverity: severity },
    });
  }, []);
  const updateCveRiskCalculationType = useCallback((cveRiskCalculationType) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: { cveRiskCalculationType },
    });
  }, []);
  const updateSettingsAction = useCallback((data) => {
    dispatch({
      type: Actions.UPDATE_SETTINGS,
      payload: data,
    });
  }, []);
  const updateFindingByBreakdown = useCallback(async (findingBreakdownData) => {
    setBreakDownBySeverityConfig((prevConfig) => ({
      ...prevConfig,
      breakDownBySeverity: findingBreakdownData,
    }));
    await GlobalSettingAPI.updateBreakDownBySeverity({
      breakDownBySeverity: findingBreakdownData,
    }).promise;
  }, []);

  if (showLoader) {
    return <AppLoader />;
  }

  return (
    <GlobalSettingsContext.Provider
      value={{
        ...state,
        findingBreakdownGroupByField:
          breakDownBySeverityConfig.breakDownBySeverity
            ? 'severity'
            : 'riskRating',
        updateFindingByBreakdown,
        breakDownBySeverity: breakDownBySeverityConfig.breakDownBySeverity,
        updateSettings,
        updateDisableStatusSettings,
        updateDisableEnvironmentSettings,
        getAllowedSeverities,
        updateRiskScoreEnabled,
        updateRiskScoreRanges,
        updateSettingsAction,
        updateCveRiskCalculationType,
        updateAnyaAIAssistantEnabled,
        updateTierBasedTagsSettings,
        updateRunbookFailureNotificationSeverity,
      }}
    >
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const withGlobalSettingsContext =
  (Component: React.ComponentType<{ globalSettings: GlobalSettingsState }>) =>
  (props: GlobalSettingsContextType) => (
    <GlobalSettingsContext.Consumer>
      {(context) => <Component globalSettings={context} {...props} />}
    </GlobalSettingsContext.Consumer>
  );

export function useGlobalSettingsContext() {
  return useContext(GlobalSettingsContext);
}

export function getGlobalSetting_UNSAFE() {
  return _.cloneDeep(GLOBAL_SETTINGS);
}

export const useHasDisabledEnvironment = () => {
  const globalSettings = useGlobalSettingsContext();
  const hasDisabledEnvironment = !isEmpty(globalSettings.disabledEnvironment);
  const envValue =
    globalSettings.disabledEnvironment[0] === 'Production'
      ? 'Staging'
      : 'Production';
  return { hasDisabledEnvironment, envValue };
};
